import z, { ZodType } from 'zod'

export const mimeType = z
    .string()
    // .nullable()
    // .default(null)
    .describe('MIME type of uploaded document')
export const zodContact = z
    .string('Please enter a valid Australian mobile (e.g. 04** *** ***)')
    .length(12, 'Please enter a valid Australian mobile (e.g. 04** *** ***)')
    .regex(
        /^04\d{2} \d{3} \d{3}$/,
        'Please enter a valid Australian mobile (e.g. 04** *** ***)'
    )
    .nullable()
    .default(null)

export const accreditationDocSchema = z.object({
    accreditationType: z.string(),
    expiryDate: z.string(),
    dateSubmitted: z.string(),
    imageURL: z.string(),
    reference: z.string(),
    type: z.string(),

    docTitle: z.string().optional(),
    qualification: z.string().optional(),
    registrationDate: z.string().optional(),
})

export const fieldStates = z
    .enum([
        'approved',
        'submitted',
        'rejected',
        'expired',
        'incomplete',
    ] as const)
    .default('incomplete')
export type FieldState_v2 = z.infer<typeof fieldStates>

const fieldSchema = <T>(e: ZodType<T>, defaultValue: T) =>
    z
        .object({
            value: e,
            state: fieldStates,
        })
        .default({
            value: defaultValue,
            state: 'incomplete',
        })
export const workingRightsTypes = z.enum([
    'unrestrictedCitizen',
    'unrestrictedPermanentResident',
    'temporaryVisaHolder',
] as const)
export type WorkingRightsType = z.infer<typeof workingRightsTypes>

export const workingRightsSchema = z.object({
    state: fieldStates,
    value: workingRightsTypes.nullable().default(null),
    type: mimeType.nullable().default(null),
    url: z.string().nullable().default(null),
    docTitle: z.string().nullable().default(null),
    reference: z.string().nullable().default(null),
})

export const workingRightsDataSchema = workingRightsSchema.optional().transform(
    (v) =>
        v ?? {
            state: 'incomplete' as FieldState_v2,
            value: null,
            type: null,
            url: null,
            docTitle: null,
            reference: null,
        }
)

export type WorkingRights = z.infer<typeof workingRightsSchema>

// export const basicFieldSchema = fieldSchema(z.string(), '')
// export type BasicField = z.infer<typeof basicFieldSchema>

export const identityDocumentTypes = z.enum([
    'passport',
    'birthCertificate',
    'australianCitizenshipCertificate',
    'driversLicence',
    'governmentEmployeeIdentityCard',
    'workingWithChildrens',
    'photoHolidingIdentification',
] as const)

export const identityDocumentTypeLabels: Record<
    z.infer<typeof identityDocumentTypes>,
    string
> = {
    passport: 'Passport',
    birthCertificate: 'Birth Certificate',
    australianCitizenshipCertificate: 'Australian Citizenship Certificate',
    driversLicence: 'Drivers Licence',
    governmentEmployeeIdentityCard: 'Government Employee Identity Card',
    workingWithChildrens: 'Working with Childrens',
    photoHolidingIdentification: '',
}

const primaryIdentityDocumentTypes = identityDocumentTypes.extract([
    'passport',
    'birthCertificate',
    'australianCitizenshipCertificate',
])
const secondaryIdentityDocumentTypes = identityDocumentTypes.exclude([
    'photoHolidingIdentification',
])
export type IdentityDocumentType = z.infer<typeof identityDocumentTypes>
export type PrimaryIdentityDocumentType = z.infer<
    typeof primaryIdentityDocumentTypes
>
export type SecondaryIdentityDocumentType = z.infer<
    typeof secondaryIdentityDocumentTypes
>

export const identificationDocumentSchema = z.object({
    type: mimeType.nullable().default(null),
    documentType: identityDocumentTypes.nullable().default(null),

    country: z.string().nullable().default(null),
    name: z.string().nullable().default(null),

    birthDate: z.string().nullable().default(null),
    reference: z.string().nullable().default(null),
    // Doc url
    docTitle: z.string().nullable().default(null),

    state: fieldStates,
    value: z.string().nullable().default(null),
})

export type IdentificationDocument = z.infer<
    typeof identificationDocumentSchema
>
export const identificationDocumentsSchema = z
    .record(z.string(), identificationDocumentSchema)
    .default({})
export type IdentificationDocuments = z.infer<
    typeof identificationDocumentsSchema
>

export const namesSchema = z.object({
    firstName: fieldSchema(z.string(), ''),
    lastName: fieldSchema(z.string(), ''),
    firstNameActual: fieldSchema(z.string(), ''),
})
export type TeacherNames = z.infer<typeof namesSchema>

export const operatingCountries = new Set(['Australia'])

export const userLocationSchema = z
    .object({
        address: z.string().default(''),
        country: z.string().default('Australia'),
        homeState: z.string().default('VIC'),
        lat: z.number().default(0),
        long: z.number().default(0),
        postCode: z.string().default(''),
        suburb: z.string().default(''),
        state: fieldStates,
    })
    .check((ctx) => {
        if (!operatingCountries.has(ctx.value.country)) {
            ctx.issues.push({
                code: 'custom',
                message: 'We do not operate in this location',
                input: ctx.value.country,
            })
        }
    })
export type UserLocationType = z.infer<typeof userLocationSchema>

export const contactSchema = z.object({
    email: fieldSchema(z.email(), ''),
    contact: fieldSchema(zodContact, ''),
    homeLocation: userLocationSchema,
})
export type TeacherContact = z.infer<typeof contactSchema>

export const profilePictureSchema = z.object({
    type: mimeType.nullable().default(null),
    state: fieldStates,
    value: z.string().nullable().default(null),
})
export const profilePictureDataSchema = profilePictureSchema.default({
    type: null,
    state: 'incomplete',
    value: null,
})

export type TeacherProfilePicture = z.infer<typeof profilePictureSchema>

export const identitySchema = z.object({
    primary: identificationDocumentSchema,
    secondary: identificationDocumentSchema,
    photoHoldingId: identificationDocumentSchema.pick({
        type: true,
        documentType: true,
        value: true,
        state: true,
    }),
})
export type TeacherIdentity = z.infer<typeof identitySchema>

export const referenceSchema = z.object({
    requestedInterview: z.boolean().nullable().default(null),
    firstName: z.string().nullable().default(null),
    lastName: z.string().nullable().default(null),
    email: z.email().nullable().default(null),

    whenWeLastWorkedTogether: z
        .string()
        .nullable()
        .default(null)
        .describe('Date string formatted as MM-YYYY'),
    whereWeWorkedTogether: z.string().nullable().default(null),
    workingRelationship: z.string().nullable().default(null),
    workplaceUid: z.string().nullable().default(null),

    state: fieldStates,
})
export const referenceDataSchema = referenceSchema.default({
    state: 'incomplete',
    requestedInterview: null,
    firstName: null,
    lastName: null,
    email: null,

    whenWeLastWorkedTogether: null,
    whereWeWorkedTogether: null,
    workingRelationship: null,
    workplaceUid: null,
})

export type TeacherReference = z.infer<typeof referenceSchema>

export const emergencyContactSchema = z.object({
    name: z
        .string()
        .min(2, 'This field requires at least 2 characters.')
        .nullable()
        .default(null),
    relationship: z
        .string()
        .min(2, 'This field requires at least 2 characters.')
        .nullable()
        .default(null),
    contact: zodContact,
    state: fieldStates,
})
export const emergencyContactDataSchema = emergencyContactSchema.default({
    name: null,
    relationship: null,
    contact: null,
    state: 'incomplete',
})
export type TeacherEmergencyContact = z.infer<typeof emergencyContactSchema>

export const emergencyContactsSchema = z.object({
    primary: emergencyContactSchema,
    secondary: emergencyContactSchema,
})

export const emergencyContactsDataSchema = z
    .object({
        primary: emergencyContactDataSchema,
        secondary: emergencyContactDataSchema,
    })
    .default({
        primary: {
            name: null,
            relationship: null,
            contact: null,
            state: 'incomplete',
        },
        secondary: {
            name: null,
            relationship: null,
            contact: null,
            state: 'incomplete',
        },
    })
export type TeacherEmergencyContacts = z.infer<typeof emergencyContactsSchema>

export const serviceBandTypes = z.enum([
    'band1',
    'band2',
    'band3',
    "I'm Unsure",
] as const)

const serviceSchema = z.object({
    timeUploaded: z.number(),
    urls: z
        .record(
            z.string(),
            z.object({ docTitle: z.string(), type: mimeType, value: z.url() })
        )
        .nullable()
        .default(null),
    uploadedBy: z.string().nullable().default(null),
    userLevelNomination: serviceBandTypes.nullable().default(null),
    userLocation: z.string().nullable().default('NSW'),
})
export const servicesSchema = z.record(z.string(), serviceSchema)
export const servicesDataSchema = servicesSchema.default({})

export type TeacherService = z.infer<typeof serviceSchema>
export type TeacherServices = z.infer<typeof servicesSchema>

export const teacherRegSchema = z.object({
    basicDetails: z.object({
        names: z.object({
            firstName: z.string().min(2),
            lastName: z.string().min(2),
            preferredName: z.union([z.string().min(2), z.string().length(0)]),
        }),
        contactInformation: z.object({
            email: z.email(),
            contact: zodContact,
            address: z.string().min(2, 'Address is required'),
        }),
    }),
    profilePhoto: z.object({
        value: z.string(),
    }),
    workingRights: workingRightsSchema,
    identity: z.object({
        primary: identificationDocumentSchema,
        secondary: identificationDocumentSchema,
        photoHoldingId: identificationDocumentSchema.pick({
            type: true,
            documentType: true,
            value: true,
        }),
    }),
    accreditation: z.record(z.string(), accreditationDocSchema),
    reference: z.object({
        firstName: z.string(),
        lastName: z.string(),
        email: z.email(),
    }),
    emergencyContacts: z.object({
        primary: emergencyContactSchema,
        secondary: emergencyContactSchema,
    }),
})

export type TeacherOnboardingType = z.infer<typeof teacherRegSchema>
